import { Component, OnInit, inject, signal, computed, LOCALE_ID } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { PublicOffer, PublicOffersParams } from '../../home-page/offers/offers.model';
import { OffersService } from '../../../../services/offers/offers.service';
import { ProductsFileMgt } from '../../../../services/product/file-management/products.file-mgt.service';
import { Loading } from '../../../../utils/loading/loading';
import { Router } from '@angular/router';

@Component({
  selector: 'app-all-offers-component',
  standalone: true,
  imports: [CommonModule, Loading, FormsModule],
  templateUrl: './all-offers-component.html',
  styleUrl: './all-offers-component.scss',
})
export class AllOffersComponent implements OnInit {
  private offersService = inject(OffersService);
  private fileMgt = inject(ProductsFileMgt);
  private locale = inject(LOCALE_ID);
  private router = inject(Router);

  isArabic = computed(() => this.locale.startsWith('ar'));
  offerImages = signal<Record<string, string>>({});
  offers = signal<PublicOffer[]>([]);
  loading = signal(false);
  currentPage = 1;
  pageSize = 10;
  totalItems = signal(0);
  itemsPerPage = computed(() => this.pageSize);
  mathMin = Math.min;

  get totalPages(): number {
    return Math.ceil(this.totalItems() / this.pageSize);
  }

  ngOnInit() {
    this.loadAllOffers();
  }

  loadAllOffers() {
    this.loading.set(true);
    const queryParams: PublicOffersParams = {
      pageNum: this.currentPage - 1,
      pageSize: this.pageSize,
    };
    this.offersService.getPublicOffers(queryParams).subscribe({
      next: (res) => {
        this.offers.set(res.list);
        this.totalItems.set(res.total);
        if (res.list?.length > 0) this.loadImages(res.list);
        this.loading.set(false);
      },
      error: () => this.loading.set(false),
    });
  }

  onPageSizeChange() {
    this.currentPage = 1;
    this.loadAllOffers();
  }

  goToPage(page: number) {
    if (page !== -1 && page !== this.currentPage) {
      this.currentPage = page;
      this.loadAllOffers();
      window.scrollTo(0, 0);
    }
  }

  nextPage() {
    if (this.currentPage < this.totalPages) {
      this.goToPage(this.currentPage + 1);
    }
  }

  previousPage() {
    if (this.currentPage > 1) {
      this.goToPage(this.currentPage - 1);
    }
  }

  getPageNumbers(): number[] {
    const pages: number[] = [];
    const total = this.totalPages;
    const current = this.currentPage;

    if (total <= 7) {
      for (let i = 1; i <= total; i++) pages.push(i);
    } else {
      pages.push(1);
      if (current > 3) pages.push(-1);
      const start = Math.max(2, current - 1);
      const end = Math.min(total - 1, current + 1);
      for (let i = start; i <= end; i++) pages.push(i);
      if (current < total - 2) pages.push(-1);
      pages.push(total);
    }
    return pages;
  }

  offerKey(offer: PublicOffer): string {
    return `${offer.sourceType}_${offer.id}`;
  }

  private loadImages(offers: PublicOffer[]) {
    offers.forEach((offer) => {
      const key = this.offerKey(offer);
      if (offer.profileImageFid && !this.offerImages()[key]) {
        this.fileMgt.getImageUrl(offer.profileImageFid).subscribe({
          next: (img) => {
            this.offerImages.update((prev) => ({ ...prev, [key]: img.url as string }));
          },
        });
      }
    });
  }

  getImage(offer: PublicOffer): string {
    return this.offerImages()[this.offerKey(offer)] || '';
  }

  onNavigateToDetails(offer: PublicOffer) {
    if (offer.sourceType === 'OFFER') {
      this.router.navigate(['/user/offers/view-offer', offer.id]);
    }
    if (offer.sourceType === 'BUNDLE') {
      this.router.navigate(['/user/offers/view-bundle', offer.id]);
    }
  }
}